(() => {
  const REVIEWS_TAB_NAME = 'reviews';
  const REVIEW_HASH_PATTERN = /^#(?:judgeme_product_reviews|jdgm-reviews|shopify-product-reviews)$/i;
  const REVIEW_BADGE_SELECTOR =
    '.jdgm-preview-badge, .jdgm-prev-badge, .jdgm-prev-badge__stars, .jdgm-prev-badge__text, .jdgm-widget.jdgm-preview-badge';
  const REVIEW_LINK_SELECTOR =
    'a[href*="#judgeme_product_reviews"], a[href*="#jdgm-reviews"], a[href*="#shopify-product-reviews"], .jdgm-preview-badge a, .jdgm-preview-badge__link';
  const REVIEW_SCROLL_FALLBACK_MS = 2500;

  function isReviewHash(hash) {
    return REVIEW_HASH_PATTERN.test(hash || '');
  }

  function getReviewHashFromLink(link) {
    if (!link?.href) return '';

    try {
      return new URL(link.href, window.location.href).hash;
    } catch {
      return link.getAttribute('href') || '';
    }
  }

  function getProductTabs() {
    return document.querySelector('product-tabs');
  }

  function getPathname(url) {
    try {
      return new URL(url, window.location.origin).pathname;
    } catch {
      return null;
    }
  }

  function isOtherProductReviewClick(target) {
    const badge = target.closest('.jdgm-widget.jdgm-preview-badge[data-product-url]');
    if (!badge) return false;

    const badgePath = getPathname(badge.dataset.productUrl);
    if (!badgePath) return false;

    return badgePath !== window.location.pathname;
  }

  function isReviewLinkForCurrentProduct(link) {
    if (!link?.href) return true;

    try {
      const linkUrl = new URL(link.href, window.location.href);
      if (!isReviewHash(linkUrl.hash)) return false;

      return linkUrl.pathname === window.location.pathname;
    } catch {
      return true;
    }
  }

  function isReviewBadgeClick(target) {
    return Boolean(target.closest(REVIEW_BADGE_SELECTOR));
  }

  function isReviewNavigationTarget(target) {
    if (isOtherProductReviewClick(target)) return false;

    const link = target.closest(REVIEW_LINK_SELECTOR);

    if (link) {
      if (!isReviewHash(getReviewHashFromLink(link))) return false;

      return isReviewLinkForCurrentProduct(link);
    }

    return isReviewBadgeClick(target);
  }

  function clearStaleReviewHash() {
    if (!isReviewHash(window.location.hash)) return;

    const url = `${window.location.pathname}${window.location.search}`;
    window.history.replaceState(null, '', url);
  }

  function openReviewsFromHash() {
    if (!isReviewHash(window.location.hash)) return;

    const productTabs = getProductTabs();
    if (!productTabs) return;

    productTabs.openReviews({ scroll: true });
    clearStaleReviewHash();
  }

  function handleReviewNavigation(event) {
    const productTabs = getProductTabs();
    if (!productTabs) return;

    if (event.type === 'hashchange') {
      if (!isReviewHash(window.location.hash)) return;
      productTabs.openReviews({ scroll: true });
      clearStaleReviewHash();
      return;
    }

    if (event.type === 'keydown') {
      if (event.key !== 'Enter' && event.key !== ' ') return;
      if (!event.target.closest('a, button, [role="link"]')) return;
    }

    if (!isReviewNavigationTarget(event.target)) return;
    if (event.type === 'click' && event.button !== 0) return;

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    productTabs.openReviews({ scroll: true });
  }

  function setupReviewNavigation() {
    if (setupReviewNavigation.initialized) return;
    setupReviewNavigation.initialized = true;

    // Capture phase intercepts Judge.me handlers before they scroll to a hidden tab panel.
    document.addEventListener('click', handleReviewNavigation, true);
    document.addEventListener('keydown', handleReviewNavigation, true);
    window.addEventListener('hashchange', handleReviewNavigation);
  }

  if (customElements.get('product-tabs')) {
    setupReviewNavigation();
    openReviewsFromHash();
    return;
  }

  class ProductTabs extends HTMLElement {
    constructor() {
      super();
      this.onTabClick = this.onTabClick.bind(this);
      this.onKeyDown = this.onKeyDown.bind(this);
    }

    connectedCallback() {
      this.tabs = Array.from(this.querySelectorAll('[data-product-tab-trigger]'));
      this.panels = Array.from(this.querySelectorAll('[data-product-tab-panel]'));

      this.tabs.forEach((tab) => {
        tab.addEventListener('click', this.onTabClick);
        tab.addEventListener('keydown', this.onKeyDown);
      });

      if (isReviewHash(window.location.hash)) {
        this.openReviews({ scroll: true });
        clearStaleReviewHash();
      }
    }

    disconnectedCallback() {
      this.tabs.forEach((tab) => {
        tab.removeEventListener('click', this.onTabClick);
        tab.removeEventListener('keydown', this.onKeyDown);
      });

      this.clearReviewScrollWatchers();
    }

    onTabClick(event) {
      const target = event.currentTarget.dataset.productTabTrigger;
      this.activate(target);
    }

    onKeyDown(event) {
      const currentIndex = this.tabs.indexOf(event.currentTarget);
      if (currentIndex === -1) return;

      let nextIndex = null;
      if (event.key === 'ArrowRight') nextIndex = (currentIndex + 1) % this.tabs.length;
      if (event.key === 'ArrowLeft') nextIndex = (currentIndex - 1 + this.tabs.length) % this.tabs.length;
      if (event.key === 'Home') nextIndex = 0;
      if (event.key === 'End') nextIndex = this.tabs.length - 1;

      if (nextIndex === null) return;
      event.preventDefault();
      const nextTab = this.tabs[nextIndex];
      this.activate(nextTab.dataset.productTabTrigger);
      nextTab.focus();
    }

    refreshReviewsWidget() {
      if (typeof window.jdgm === 'undefined') return;

      if (typeof window.jdgm.customInit === 'function') {
        window.jdgm.customInit();
        return;
      }

      if (window.jdgm.SHOPify && typeof window.jdgm.SHOPify.init === 'function') {
        window.jdgm.SHOPify.init();
      }
    }

    getReviewsPanel() {
      return this.querySelector('[data-product-tab-panel="reviews"]');
    }

    getReviewScrollTarget() {
      const panel = this.getReviewsPanel();

      return (
        document.getElementById('judgeme_product_reviews') ||
        panel?.querySelector('#judgeme_product_reviews, .jdgm-review-widget') ||
        panel
      );
    }

    isScrollTargetVisible(target) {
      if (!target) return false;

      const panel = this.getReviewsPanel();
      if (!panel || panel.hasAttribute('hidden')) return false;
      if (target.closest('[hidden]')) return false;

      return target.getClientRects().length > 0;
    }

    clearReviewScrollWatchers() {
      if (this.reviewScrollObserver) {
        this.reviewScrollObserver.disconnect();
        this.reviewScrollObserver = null;
      }

      if (this.reviewScrollFallback) {
        window.clearTimeout(this.reviewScrollFallback);
        this.reviewScrollFallback = null;
      }
    }

    scrollToReviewTarget() {
      const target = this.getReviewScrollTarget();
      if (!this.isScrollTargetVisible(target)) return false;

      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return true;
    }

    scrollToReviewsWhenReady() {
      this.clearReviewScrollWatchers();

      const panel = this.getReviewsPanel();
      if (!panel) return;

      const finish = () => {
        this.clearReviewScrollWatchers();
      };

      const attemptScroll = () => {
        if (this.scrollToReviewTarget()) {
          finish();
          return true;
        }

        return false;
      };

      // Two animation frames let the browser apply the unhidden tab panel layout.
      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => {
          if (attemptScroll()) return;

          this.reviewScrollObserver = new MutationObserver(() => {
            attemptScroll();
          });

          this.reviewScrollObserver.observe(panel, {
            childList: true,
            subtree: true,
            attributes: true,
            attributeFilter: ['hidden', 'id', 'class', 'style'],
          });

          // Fallback for slow Judge.me rendering on mobile or low-end devices.
          this.reviewScrollFallback = window.setTimeout(() => {
            attemptScroll();
            finish();
          }, REVIEW_SCROLL_FALLBACK_MS);
        });
      });
    }

    openReviews({ scroll = true } = {}) {
      const reviewsTab = this.querySelector(`[data-product-tab-trigger="${REVIEWS_TAB_NAME}"]`);
      if (!reviewsTab) return false;

      this.activate(REVIEWS_TAB_NAME);

      if (scroll) {
        this.scrollToReviewsWhenReady();
      }

      return true;
    }

    activate(name) {
      this.tabs.forEach((tab) => {
        const isActive = tab.dataset.productTabTrigger === name;
        tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
        tab.setAttribute('tabindex', isActive ? '0' : '-1');
      });

      this.panels.forEach((panel) => {
        const isActive = panel.dataset.productTabPanel === name;
        if (isActive) {
          panel.removeAttribute('hidden');
        } else {
          panel.setAttribute('hidden', '');
        }
      });

      if (name === REVIEWS_TAB_NAME) {
        this.refreshReviewsWidget();
      }

      this.dispatchEvent(
        new CustomEvent('product-tabs:change', {
          bubbles: true,
          detail: { name },
        })
      );
    }
  }

  customElements.define('product-tabs', ProductTabs);

  setupReviewNavigation();
  openReviewsFromHash();
})();
